import { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { adminProducts, adminProdutoCategorias, adminWhatsappNumero, adminAtas } from '@/mocks/admin';
import AdminHeader from '../components/Header';
import { uploadToCloudflareR2 } from '@/services/cloudflareUpload';
import { deleteProduto, fetchAtas, fetchProdutoCategorias, fetchProdutos, saveProduto, type Ata } from '@/services/catalog';

interface ProdutoCategoria {
  id: number;
  nome: string;
}

interface ProdutoFoto {
  id: number;
  url: string;
  principal: boolean;
}

interface ProdutoVariante {
  id: number;
  tipo?: string;
  cor: string;
  tamanho: string;
  sku: string;
  estoque: number;
  ativo: boolean;
}

interface Produto {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  categorias: number[];
  estoque: number;
  vendas: number;
  fotos: ProdutoFoto[];
  variantes: ProdutoVariante[];
  descricao: string;
  atasIds: number[];
  ativo: boolean;
}

const defaultDescricao = '';

export default function ProdutosPage() {
  const [produtos, setProdutos] = useState<Produto[]>(adminProducts);
  const [categorias, setCategorias] = useState<ProdutoCategoria[]>(adminProdutoCategorias);
  const [atas, setAtas] = useState<Ata[]>(adminAtas);
  const [whatsappNumero, setWhatsappNumero] = useState(adminWhatsappNumero);
  const [showModal, setShowModal] = useState(false);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(true);
  const [categoriaSearch, setCategoriaSearch] = useState('');
  const [categoriaDropdownOpen, setCategoriaDropdownOpen] = useState(false);
  const [ataDropdownOpen, setAtaDropdownOpen] = useState(false);
  const [ataSearch, setAtaSearch] = useState('');
  const [novaCategoriaNome, setNovaCategoriaNome] = useState('');
  const [whatsappInput, setWhatsappInput] = useState(adminWhatsappNumero);
  const [descricaoMode, setDescricaoMode] = useState<'visual' | 'html' | 'preview'>('visual');
  const [tempWhatsapp, setTempWhatsapp] = useState(adminWhatsappNumero);
  const [uploadingFotos, setUploadingFotos] = useState(false);
  const [fotoUploadError, setFotoUploadError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const ataDropdownRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    nome: '',
    sku: '',
    marca: '',
    categorias: [] as number[],
    estoque: '',
    fotos: [] as ProdutoFoto[],
    variantes: [] as ProdutoVariante[],
    descricao: '',
    atasIds: [] as number[],
    ativo: true,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoriaDropdownOpen(false);
        setCategoriaSearch('');
      }
      if (ataDropdownRef.current && !ataDropdownRef.current.contains(event.target as Node)) {
        setAtaDropdownOpen(false);
        setAtaSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    Promise.all([fetchProdutos(), fetchProdutoCategorias(), fetchAtas()]).then(([produtosData, categoriasData, atasData]) => {
      setProdutos(produtosData);
      setCategorias(categoriasData);
      setAtas(atasData);
    });
  }, []);

  const filteredProdutos = produtos.filter(
    (p) =>
      (p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
      categorias.some((cat) => p.categorias.includes(cat.id) && cat.nome.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (mostrarInativos || p.ativo)
  );

  const filteredCategorias = categorias.filter((cat) =>
    cat.nome.toLowerCase().includes(categoriaSearch.toLowerCase())
  );

  const filteredAtas = atas.filter((ata) =>
    ata.numero.toLowerCase().includes(ataSearch.toLowerCase()) ||
    ata.nome.toLowerCase().includes(ataSearch.toLowerCase()) ||
    ata.orgao.toLowerCase().includes(ataSearch.toLowerCase())
  );

  const getCategoriaNomes = (ids: number[]) =>
    ids.map((id) => categorias.find((c) => c.id === id)?.nome || '').filter(Boolean).join(', ');

  const getAtaLabel = (ataId: number) => {
    const ata = atas.find((a) => a.id === ataId);
    return ata ? `${ata.numero} — ${ata.nome}` : '';
  };

  const getFotoPrincipal = (fotos: ProdutoFoto[]) =>
    fotos.find((f) => f.principal) || fotos[0] || null;

  const resetForm = () => {
    setFormData({
      nome: '',
      sku: '',
      marca: '',
      categorias: [],
      estoque: '',
      fotos: [],
      variantes: [],
      descricao: '',
      atasIds: [],
      ativo: true,
    });
    setDescricaoMode('visual');
  };

  const openNewModal = () => {
    setEditingId(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (produto: Produto) => {
    setEditingId(produto.id);
    setFormData({
      nome: produto.nome,
      sku: produto.sku,
      marca: produto.marca,
      categorias: [...produto.categorias],
      estoque: String(produto.estoque),
      fotos: produto.fotos.map((f) => ({ ...f })),
      variantes: produto.variantes.map((v) => ({ ...v })),
      descricao: produto.descricao,
      atasIds: [...produto.atasIds],
      ativo: produto.ativo,
    });
    setDescricaoMode('visual');
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal || descricaoMode !== 'visual' || !editorRef.current) return;
    if (editorRef.current.innerHTML !== formData.descricao) {
      editorRef.current.innerHTML = formData.descricao || '';
    }
  }, [showModal, descricaoMode, editingId, formData.descricao]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const estoqueNum = parseInt(formData.estoque, 10) || 0;
    const descricaoAtual =
      descricaoMode === 'visual' && editorRef.current
        ? editorRef.current.innerHTML
        : formData.descricao;

    if (editingId) {
      const updatedProduto = {
        id: editingId,
        nome: formData.nome,
        sku: formData.sku,
        marca: formData.marca,
        categorias: formData.categorias,
        estoque: estoqueNum,
        fotos: formData.fotos,
        variantes: formData.variantes,
        descricao: descricaoAtual,
        atasIds: formData.atasIds,
        ativo: formData.ativo,
      };
      try {
        await saveProduto(updatedProduto);
      } catch (error) {
        console.error('Falha ao salvar produto no Supabase.', error);
        window.alert('Nao foi possivel salvar o produto no Supabase. Verifique a conexao e tente novamente.');
        return;
      }
      setProdutos((prev) =>
        prev.map((p) =>
          p.id === editingId
            ? {
                ...p,
                nome: formData.nome,
                sku: formData.sku,
                marca: formData.marca,
                categorias: formData.categorias,
                estoque: estoqueNum,
                fotos: formData.fotos,
                variantes: formData.variantes,
                descricao: descricaoAtual,
                atasIds: formData.atasIds,
                ativo: formData.ativo,
              }
            : p
        )
      );
    } else {
      const newProduto: Produto = {
        id: Math.max(...produtos.map((p) => p.id), 0) + 1,
        nome: formData.nome,
        sku: formData.sku,
        marca: formData.marca,
        categorias: formData.categorias,
        estoque: estoqueNum,
        vendas: 0,
        fotos: formData.fotos,
        variantes: formData.variantes,
        descricao: descricaoAtual,
        atasIds: formData.atasIds,
        ativo: true,
      };
      try {
        const savedProduto = await saveProduto(newProduto);
        setProdutos((prev) => [...prev, savedProduto || newProduto]);
      } catch (error) {
        console.error('Falha ao salvar produto no Supabase.', error);
        window.alert('Nao foi possivel salvar o produto no Supabase. Verifique a conexao e tente novamente.');
        return;
      }
    }
    setShowModal(false);
  };

  const handleDelete = (id: number) => {
    if (confirm('Tem certeza que deseja excluir este produto?')) {
      deleteProduto(id).catch((error) => console.warn('Falha ao excluir no Supabase.', error));
      setProdutos((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const toggleProdutoStatus = (id: number) => {
    setProdutos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ativo: !p.ativo } : p))
    );
  };

  const handleWhatsAppClick = (produto: Produto) => {
    const baseUrl = window.location.origin;
    const mensagem = `Olá! Tenho interesse no produto: *${produto.nome}* (SKU: ${produto.sku})\n\n${baseUrl}/produto/${produto.id}`;
    window.open(`https://wa.me/${whatsappNumero}?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  const handleSaveWhatsapp = () => {
    setWhatsappNumero(tempWhatsapp);
    setShowWhatsappModal(false);
  };

  // Fotos handlers
  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const ownerName = formData.nome.trim() || formData.sku.trim() || 'produto-sem-nome';
    const newFotos: ProdutoFoto[] = [];
    setUploadingFotos(true);
    setFotoUploadError('');

    try {
      for (const file of Array.from(files)) {
        const uploaded = await uploadToCloudflareR2(file, 'produto-imagem', ownerName);
        newFotos.push({
          id: Date.now() + Math.random(),
          url: uploaded.url,
          principal: formData.fotos.length === 0 && newFotos.length === 0,
        });
      }

      setFormData((prev) => ({
        ...prev,
        fotos: [...prev.fotos, ...newFotos],
      }));
    } catch (error) {
      console.error('Falha no upload para o Cloudflare R2.', error);
      setFotoUploadError(
        error instanceof Error
          ? `Nao foi possivel enviar para o Cloudflare R2: ${error.message}`
          : 'Nao foi possivel enviar para o Cloudflare R2. Verifique a configuracao do bucket.',
      );
    } finally {
      setUploadingFotos(false);
    }
    e.target.value = '';
  };

  const handleRemoveFoto = (fotoId: number) => {
    setFormData((prev) => {
      const remaining = prev.fotos.filter((f) => f.id !== fotoId);
      if (remaining.length > 0 && !remaining.some((f) => f.principal)) {
        remaining[0].principal = true;
      }
      return { ...prev, fotos: remaining };
    });
  };

  const handleSetFotoPrincipal = (fotoId: number) => {
    setFormData((prev) => ({
      ...prev,
      fotos: prev.fotos.map((f) => ({
        ...f,
        principal: f.id === fotoId,
      })),
    }));
  };

  // Variantes handlers
  const handleAddVariante = () => {
    setFormData((prev) => ({
      ...prev,
      variantes: [
        ...prev.variantes,
        { id: Date.now() + Math.random(), tipo: '', cor: '', tamanho: '', sku: '', estoque: 0, ativo: true },
      ],
    }));
  };

  const handleRemoveVariante = (varId: number) => {
    setFormData((prev) => ({
      ...prev,
      variantes: prev.variantes.filter((v) => v.id !== varId),
    }));
  };

  const handleUpdateVariante = (varId: number, field: string, value: string | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      variantes: prev.variantes.map((v) =>
        v.id === varId ? { ...v, [field]: value } : v
      ),
    }));
  };

  // Categorias
  const toggleCategoria = (catId: number) => {
    setFormData((prev) => ({
      ...prev,
      categorias: prev.categorias.includes(catId)
        ? prev.categorias.filter((id) => id !== catId)
        : [...prev.categorias, catId],
    }));
  };

  const toggleAta = (ataId: number) => {
    setFormData((prev) => ({
      ...prev,
      atasIds: prev.atasIds.includes(ataId)
        ? prev.atasIds.filter((id) => id !== ataId)
        : [...prev.atasIds, ataId],
    }));
  };

  const handleCriarCategoria = () => {
    if (!novaCategoriaNome.trim()) return;
    const newId = Math.max(...categorias.map((c) => c.id), 0) + 1;
    const newCat: ProdutoCategoria = { id: newId, nome: novaCategoriaNome.trim() };
    setCategorias((prev) => [...prev, newCat]);
    setFormData((prev) => ({
      ...prev,
      categorias: [...prev.categorias, newId],
    }));
    setNovaCategoriaNome('');
    setShowCategoriaModal(false);
  };

  const handleDeleteCategoria = (catId: number) => {
    if (confirm('Excluir esta categoria? Ela será removida de todos os produtos vinculados.')) {
      setCategorias((prev) => prev.filter((c) => c.id !== catId));
      setProdutos((prev) =>
        prev.map((p) => ({
          ...p,
          categorias: p.categorias.filter((id) => id !== catId),
        }))
      );
      setFormData((prev) => ({
        ...prev,
        categorias: prev.categorias.filter((id) => id !== catId),
      }));
    }
  };

  // HTML toolbar insert
  const insertTag = useCallback((openTag: string, closeTag: string, placeholder?: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.descricao.substring(start, end);
    const textToWrap = selectedText || placeholder || '';
    const newText =
      formData.descricao.substring(0, start) +
      openTag +
      textToWrap +
      closeTag +
      formData.descricao.substring(end);

    setFormData((prev) => ({ ...prev, descricao: newText }));

    // Restore cursor after state update
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = selectedText
        ? start + openTag.length + textToWrap.length + closeTag.length
        : start + openTag.length + textToWrap.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  }, [formData.descricao]);

  // Visual editor commands
  const execVisualCommand = useCallback((command: string, value?: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand(command, false, value);
  }, []);

  const insertVisualLink = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const selectedText = selection.toString();
    const url = prompt('URL do link:', 'https://');
    if (url) {
      if (selectedText) {
        document.execCommand('createLink', false, url);
      } else {
        const linkHtml = `<a href="${url}">${url}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
      }
    }
  }, []);

  // Sync visual editor content back to formData on input
  const handleVisualInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    setFormData((prev) => ({ ...prev, descricao: editor.innerHTML }));
  }, []);

  // Convert between modes
  const switchToVisual = useCallback(() => {
    setDescricaoMode('visual');
    requestAnimationFrame(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = formData.descricao || '';
      }
    });
  }, [formData.descricao]);

  const switchToHtml = useCallback(() => {
    // Grab current visual content before switching
    if (editorRef.current && descricaoMode === 'visual') {
      setFormData((prev) => ({ ...prev, descricao: editorRef.current!.innerHTML }));
    }
    setDescricaoMode('html');
  }, [descricaoMode]);

  // Export Excel
  const handleExportExcel = () => {
    const dataToExport = filteredProdutos.length > 0 ? filteredProdutos : produtos;
    const rows = dataToExport.map((p) => ({
      'SKU': p.sku,
      'Nome': p.nome,
      'Marca': p.marca,
      'Categorias': getCategoriaNomes(p.categorias),
      'Estoque': p.estoque,
        'Variantes': p.variantes.map((v) => `${v.tipo ? `${v.tipo}: ` : ''}${v.cor} / ${v.tamanho} (${v.sku})`).join('; '),
      'Vendas': p.vendas,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 16 }, { wch: 40 }, { wch: 20 }, { wch: 30 },
      { wch: 10 }, { wch: 50 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produtos');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `produtos-donnos-${today}.xlsx`);
  };

  return (
    <div>
      <AdminHeader title="Gerenciamento de Produtos" subtitle="Cadastre e gerencie os produtos da loja" />

      <div className="p-8">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por nome, SKU, marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setTempWhatsapp(whatsappNumero);
                setShowWhatsappModal(true);
              }}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-whatsapp-line text-green-500"></i>
              Configurar WhatsApp
            </button>
            <button
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-file-excel-2-line text-donnos-green"></i>
              Exportar Excel
            </button>
            <button
              onClick={() => {
                setShowCategoriaModal(true);
                setNovaCategoriaNome('');
              }}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg border border-gray-200 transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-price-tag-3-line"></i>
              Categorias
            </button>
            <button
              onClick={openNewModal}
              className="inline-flex items-center gap-2 bg-donnos-green hover:bg-donnos-green-dark text-white font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap text-sm"
            >
              <i className="ri-add-line"></i>
              Novo Produto
            </button>
          </div>
        </div>

        {/* Filtro de status */}
        <div className="flex items-center gap-3 mb-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setMostrarInativos(!mostrarInativos)}
              className={`relative w-9 h-5 rounded-full transition-colors ${mostrarInativos ? 'bg-donnos-green' : 'bg-gray-300'}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${mostrarInativos ? 'left-[18px]' : 'left-0.5'}`}></div>
            </div>
            <span className="text-sm text-gray-600">Mostrar inativos</span>
          </label>
          <span className="text-xs text-gray-400">
            {produtos.filter((p) => !p.ativo).length} produto(s) inativo(s)
          </span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">SKU</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Produto</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Marca</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Categorias</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Estoque</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">ATAs</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Variantes</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Orçamento</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3 whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProdutos.map((produto) => {
                  const fotoPrincipal = getFotoPrincipal(produto.fotos);
                  return (
                    <tr key={produto.id} className={`border-t border-gray-50 hover:bg-gray-50/50 transition-colors ${!produto.ativo ? 'opacity-50 bg-gray-50/80' : ''}`}>
                      <td className="px-5 py-3">
                        <span className="text-sm font-mono font-semibold text-gray-900 whitespace-nowrap">{produto.sku}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {fotoPrincipal && (
                            <img
                              src={fotoPrincipal.url}
                              alt={produto.nome}
                              className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0"
                            />
                          )}
                          <span className="text-sm font-medium text-gray-900 max-w-[200px] truncate">{produto.nome}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-600 whitespace-nowrap">{produto.marca}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {produto.categorias.length > 0 ? (
                            <>
                              {produto.categorias.slice(0, 2).map((catId) => {
                                const cat = categorias.find((c) => c.id === catId);
                                return cat ? (
                                  <span key={catId} className="inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs whitespace-nowrap">
                                    {cat.nome}
                                  </span>
                                ) : null;
                              })}
                              {produto.categorias.length > 2 && (
                                <span className="inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">+{produto.categorias.length - 2}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-sm font-medium ${produto.estoque < 20 ? 'text-red-500' : 'text-gray-900'}`}>
                          {produto.estoque}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {produto.atasIds.length > 0 ? (
                            <>
                              {produto.atasIds.slice(0, 2).map((ataId) => {
                                const ata = atas.find((a) => a.id === ataId);
                                return ata ? (
                                  <span key={ataId} className="inline-flex px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs whitespace-nowrap border border-amber-200" title={ata.nome}>
                                    {ata.numero}
                                  </span>
                                ) : null;
                              })}
                              {produto.atasIds.length > 2 && (
                                <span className="inline-flex px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs">+{produto.atasIds.length - 2}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-600">{produto.variantes.length}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div
                          onClick={() => toggleProdutoStatus(produto.id)}
                          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${produto.ativo ? 'bg-donnos-green' : 'bg-gray-300'}`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${produto.ativo ? 'left-[22px]' : 'left-0.5'}`}></div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => handleWhatsAppClick(produto)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 hover:bg-green-100 text-green-600 text-xs font-medium transition-colors whitespace-nowrap"
                        >
                          <i className="ri-whatsapp-line"></i>
                          Solicitar Orçamento
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openEditModal(produto)}
                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            title="Editar"
                          >
                            <i className="ri-edit-line text-gray-600 text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(produto.id)}
                            className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredProdutos.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-shopping-bag-3-line text-4xl text-gray-300 block mb-3"></i>
              <p className="text-gray-500">Nenhum produto encontrado</p>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Exibindo {filteredProdutos.length} de {produtos.length} produtos
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-10 pb-10">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-xl">
              <h3 className="text-lg font-bold text-gray-900">
                {editingId ? 'Editar Produto' : 'Novo Produto'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Nome + SKU + Marca */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Produto <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Ex: Notebook Dell Inspiron 15"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="Ex: NB-DELL-001"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                    placeholder="Ex: Dell"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
              </div>

              {/* Status do Produto */}
              <div className="flex items-center gap-3 py-2">
                <label className="text-sm font-medium text-gray-700">Status do Produto:</label>
                <div
                  onClick={() => setFormData({ ...formData, ativo: !formData.ativo })}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${formData.ativo ? 'bg-donnos-green' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${formData.ativo ? 'left-[22px]' : 'left-0.5'}`}></div>
                </div>
                <span className={`text-sm font-medium ${formData.ativo ? 'text-donnos-green' : 'text-gray-400'}`}>
                  {formData.ativo ? 'Ativo' : 'Inativo'}
                </span>
                {!formData.ativo && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                    Produto oculto da loja
                  </span>
                )}
              </div>

              {/* Categorias + Estoque */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div ref={dropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categorias</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setCategoriaDropdownOpen(!categoriaDropdownOpen);
                        setCategoriaSearch('');
                      }}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-left focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white flex items-center justify-between"
                    >
                      <span className={formData.categorias.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                        {formData.categorias.length === 0
                          ? 'Selecione uma ou mais categorias...'
                          : `${formData.categorias.length} categoria(s) selecionada(s)`}
                      </span>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>

                    {categoriaDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden flex flex-col">
                        <div className="p-2 border-b border-gray-100">
                          <div className="relative">
                            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                            <input
                              type="text"
                              placeholder="Buscar categoria..."
                              value={categoriaSearch}
                              onChange={(e) => setCategoriaSearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:border-donnos-green"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-40">
                          {filteredCategorias.length === 0 && categoriaSearch && (
                            <div className="px-3 py-4 text-center">
                              <p className="text-xs text-gray-400 mb-2">Nenhuma categoria encontrada</p>
                              <button
                                type="button"
                                onClick={() => {
                                  setNovaCategoriaNome(categoriaSearch);
                                  setCategoriaDropdownOpen(false);
                                  setShowCategoriaModal(true);
                                }}
                                className="text-xs text-donnos-green hover:text-donnos-green-dark font-medium"
                              >
                                + Criar &quot;{categoriaSearch}&quot;
                              </button>
                            </div>
                          )}
                          {filteredCategorias.length === 0 && !categoriaSearch && (
                            <p className="px-3 py-4 text-xs text-gray-400 text-center">Nenhuma categoria cadastrada</p>
                          )}
                          {filteredCategorias.map((cat) => {
                            const selected = formData.categorias.includes(cat.id);
                            return (
                              <label
                                key={cat.id}
                                className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleCategoria(cat.id)}
                                  className="w-4 h-4 rounded border-gray-300 text-donnos-green focus:ring-donnos-green"
                                />
                                <span className="text-sm text-gray-700">{cat.nome}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="p-2 border-t border-gray-100">
                          <button
                            type="button"
                            onClick={() => {
                              setCategoriaDropdownOpen(false);
                              setShowCategoriaModal(true);
                              setNovaCategoriaNome('');
                            }}
                            className="w-full text-center text-xs text-donnos-green hover:text-donnos-green-dark font-medium py-1.5 rounded-md hover:bg-donnos-green/5 transition-colors"
                          >
                            <i className="ri-add-line mr-1"></i>
                            Criar nova categoria
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {formData.categorias.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {formData.categorias.map((catId) => {
                        const cat = categorias.find((c) => c.id === catId);
                        return cat ? (
                          <span key={catId} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-donnos-green/10 text-donnos-green text-xs font-medium">
                            {cat.nome}
                            <button type="button" onClick={() => toggleCategoria(catId)} className="w-4 h-4 rounded-full hover:bg-donnos-green/20 flex items-center justify-center">
                              <i className="ri-close-line text-[10px]"></i>
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estoque</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={formData.estoque}
                    onChange={(e) => setFormData({ ...formData, estoque: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
              </div>

              {/* ATAs / Editais */}
              <div ref={ataDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <i className="ri-file-text-line mr-1 text-gray-400"></i>
                  ATAs / Editais Vinculados
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setAtaDropdownOpen(!ataDropdownOpen);
                      setAtaSearch('');
                    }}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-left focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green bg-white flex items-center justify-between"
                  >
                    <span className={formData.atasIds.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
                      {formData.atasIds.length === 0
                        ? 'Selecione ATAs ou editais vinculados a este produto...'
                        : `${formData.atasIds.length} ATA(s) vinculada(s)`}
                    </span>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${ataDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {ataDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                      <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                          <input
                            type="text"
                            placeholder="Buscar por número, nome ou órgão..."
                            value={ataSearch}
                            onChange={(e) => setAtaSearch(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:border-donnos-green"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredAtas.length === 0 && (
                          <p className="px-3 py-4 text-xs text-gray-400 text-center">Nenhuma ATA encontrada</p>
                        )}
                        {filteredAtas.map((ata) => {
                          const selected = formData.atasIds.includes(ata.id);
                          return (
                            <label
                              key={ata.id}
                              className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleAta(ata.id)}
                                className="w-4 h-4 mt-0.5 rounded border-gray-300 text-donnos-green focus:ring-donnos-green flex-shrink-0"
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 truncate">{ata.numero}</span>
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                    ata.status === 'Ativa' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {ata.status}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{ata.nome}</p>
                                <p className="text-xs text-gray-400 truncate">{ata.orgao} — UASG {ata.uasg}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {formData.atasIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.atasIds.map((ataId) => {
                      const ata = atas.find((a) => a.id === ataId);
                      return ata ? (
                        <span key={ataId} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                          {ata.numero}
                          <button type="button" onClick={() => toggleAta(ataId)} className="w-4 h-4 rounded-full hover:bg-amber-200 flex items-center justify-center">
                            <i className="ri-close-line text-[10px]"></i>
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Fotos */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Fotos do Produto</label>
                  <span className="text-xs text-gray-400">{formData.fotos.length} foto(s)</span>
                </div>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-donnos-green/40 transition-colors">
                  <input
                    type="file"
                    id="produto-fotos"
                    multiple
                    onChange={handleFotoUpload}
                    className="hidden"
                    accept="image/*"
                    disabled={uploadingFotos}
                  />
                  <label htmlFor="produto-fotos" className={uploadingFotos ? 'cursor-wait opacity-70' : 'cursor-pointer'}>
                    <i className="ri-image-add-line text-2xl text-gray-300 block mb-1"></i>
                    <p className="text-sm text-gray-500">
                      <span className="text-donnos-green font-medium">
                        {uploadingFotos ? 'Enviando para o Cloudflare R2...' : 'Clique para adicionar fotos'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">JPG, PNG ou WebP — até 5MB cada</p>
                  </label>
                </div>
                {fotoUploadError && (
                  <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {fotoUploadError}
                  </div>
                )}

                {formData.fotos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    {formData.fotos.map((foto) => (
                      <div
                        key={foto.id}
                        className={`relative group rounded-lg overflow-hidden border-2 transition-colors ${
                          foto.principal ? 'border-donnos-green' : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <img
                          src={foto.url}
                          alt="Foto do produto"
                          className="w-full h-32 object-cover bg-gray-100"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => handleSetFotoPrincipal(foto.id)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs transition-colors ${
                              foto.principal ? 'bg-donnos-green' : 'bg-black/50 hover:bg-donnos-green'
                            }`}
                            title="Definir como principal"
                          >
                            <i className={foto.principal ? 'ri-star-fill' : 'ri-star-line'}></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveFoto(foto.id)}
                            className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-500 flex items-center justify-center text-white text-xs transition-colors"
                            title="Remover foto"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                        {foto.principal && (
                          <span className="absolute top-1.5 left-1.5 bg-donnos-green text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                            Principal
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Variantes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Variantes</label>
                  <button
                    type="button"
                    onClick={handleAddVariante}
                    className="inline-flex items-center gap-1 text-xs text-donnos-green hover:text-donnos-green-dark font-medium"
                  >
                    <i className="ri-add-line"></i>
                    Adicionar variante
                  </button>
                </div>

                {formData.variantes.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-gray-400">Nenhuma variante cadastrada. Adicione variações como cor e tamanho.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.variantes.map((variante) => (
                      <div
                        key={variante.id}
                        className={`grid grid-cols-1 gap-2 rounded-lg p-3 md:grid-cols-[1.1fr_1fr_1fr_0.9fr_90px_auto_auto] md:items-center ${variante.ativo ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`}
                      >
                        <input
                          type="text"
                          value={variante.tipo || ''}
                          onChange={(e) => handleUpdateVariante(variante.id, 'tipo', e.target.value)}
                          placeholder="Tipo da variante"
                          className="px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-donnos-green"
                        />
                        <input
                          type="text"
                          value={variante.cor}
                          onChange={(e) => handleUpdateVariante(variante.id, 'cor', e.target.value)}
                          placeholder="Nome/valor"
                          className="px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-donnos-green"
                        />
                        <input
                          type="text"
                          value={variante.tamanho}
                          onChange={(e) => handleUpdateVariante(variante.id, 'tamanho', e.target.value)}
                          placeholder="Complemento"
                          className="px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-donnos-green"
                        />
                        <input
                          type="text"
                          value={variante.sku}
                          onChange={(e) => handleUpdateVariante(variante.id, 'sku', e.target.value)}
                          placeholder="SKU"
                          className="px-3 py-2 rounded-md border border-gray-200 text-sm font-mono focus:outline-none focus:border-donnos-green"
                        />
                        <input
                          type="number"
                          min="0"
                          value={variante.estoque}
                          onChange={(e) => handleUpdateVariante(variante.id, 'estoque', parseInt(e.target.value, 10) || 0)}
                          placeholder="Estoque"
                          className="px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-donnos-green"
                        />
                        <div
                          onClick={() => handleUpdateVariante(variante.id, 'ativo', !variante.ativo)}
                          className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${variante.ativo ? 'bg-donnos-green' : 'bg-gray-300'}`}
                          title={variante.ativo ? 'Ativo' : 'Inativo'}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${variante.ativo ? 'left-[18px]' : 'left-0.5'}`}></div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariante(variante.id)}
                          className="w-8 h-8 rounded-md hover:bg-red-50 flex items-center justify-center transition-colors flex-shrink-0"
                        >
                          <i className="ri-close-line text-red-400"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Descrição com editor Visual / HTML / Preview */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Descrição</label>
                  {/* Mode Switcher */}
                  <div className="inline-flex bg-gray-100 rounded-full p-0.5">
                    <button
                      type="button"
                      onClick={switchToVisual}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        descricaoMode === 'visual'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Visual
                    </button>
                    <button
                      type="button"
                      onClick={switchToHtml}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        descricaoMode === 'html'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className="ri-code-line mr-1"></i>
                      HTML
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (descricaoMode === 'visual' && editorRef.current) {
                          setFormData((prev) => ({ ...prev, descricao: editorRef.current!.innerHTML }));
                        }
                        setDescricaoMode('preview');
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        descricaoMode === 'preview'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className="ri-eye-line mr-1"></i>
                      Preview
                    </button>
                  </div>
                </div>

                {/* Preview Mode */}
                {descricaoMode === 'preview' && (
                  <div className="border border-gray-200 rounded-lg p-4 min-h-[180px] bg-white">
                    {formData.descricao ? (
                      <div
                        className="prose prose-sm max-w-none text-gray-700 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mb-2 [&_h3]:mt-4 [&_p]:mb-2 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-4 [&_blockquote]:text-gray-500 [&_blockquote]:italic [&_a]:text-donnos-green [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: formData.descricao }}
                      />
                    ) : (
                      <p className="text-gray-400 italic text-sm">Nenhuma descrição informada</p>
                    )}
                  </div>
                )}

                {/* Visual Mode — WYSIWYG */}
                {descricaoMode === 'visual' && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:border-donnos-green focus-within:ring-1 focus-within:ring-donnos-green transition-all">
                    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-100 flex-wrap">
                      <button type="button" onClick={() => execVisualCommand('bold')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Negrito">
                        <i className="ri-bold text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => execVisualCommand('italic')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Itálico">
                        <i className="ri-italic text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => execVisualCommand('underline')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Sublinhado">
                        <i className="ri-underline text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => execVisualCommand('formatBlock', '<h3>')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Título">
                        <i className="ri-heading text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => execVisualCommand('formatBlock', '<p>')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Parágrafo">
                        <i className="ri-paragraph text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => execVisualCommand('insertUnorderedList')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Lista com marcadores">
                        <i className="ri-list-unordered text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => execVisualCommand('insertOrderedList')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Lista numerada">
                        <i className="ri-list-ordered text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={insertVisualLink} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Inserir link">
                        <i className="ri-link text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => execVisualCommand('formatBlock', '<blockquote>')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Citação">
                        <i className="ri-double-quotes-l text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => execVisualCommand('removeFormat')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Limpar formatação">
                        <i className="ri-format-clear text-gray-600 text-sm"></i>
                      </button>
                    </div>
                    <div
                      ref={editorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleVisualInput}
                      className="w-full px-4 py-3 min-h-[180px] text-sm text-gray-700 focus:outline-none bg-white [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mb-1 [&_h3]:mt-3 [&_p]:mb-1.5 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-1.5 [&_li]:mb-0.5 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-500 [&_blockquote]:italic [&_blockquote]:my-2 [&_a]:text-donnos-green [&_a]:underline"
                      data-placeholder="Descreva seu produto aqui... Use a toolbar acima para formatar."
                    />
                  </div>
                )}

                {/* HTML Mode */}
                {descricaoMode === 'html' && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden focus-within:border-donnos-green focus-within:ring-1 focus-within:ring-donnos-green transition-all">
                    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-100 flex-wrap">
                      <button type="button" onClick={() => insertTag('<strong>', '</strong>', 'texto em negrito')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Negrito">
                        <i className="ri-bold text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => insertTag('<em>', '</em>', 'texto em itálico')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Itálico">
                        <i className="ri-italic text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => insertTag('<h3>', '</h3>', 'Título da seção')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Título (H3)">
                        <i className="ri-heading text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => insertTag('<ul>\n  <li>', '</li>\n</ul>', 'item da lista')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Lista com marcadores">
                        <i className="ri-list-unordered text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => insertTag('<ol>\n  <li>', '</li>\n</ol>', 'item numerado')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Lista numerada">
                        <i className="ri-list-ordered text-gray-600 text-sm"></i>
                      </button>
                      <div className="w-px h-5 bg-gray-200 mx-1"></div>
                      <button type="button" onClick={() => insertTag('<a href="', '">texto do link</a>', 'https://')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Inserir link">
                        <i className="ri-link text-gray-600 text-sm"></i>
                      </button>
                      <button type="button" onClick={() => insertTag('<blockquote>', '</blockquote>', 'citação')} className="w-8 h-7 rounded hover:bg-gray-200 flex items-center justify-center transition-colors" title="Citação">
                        <i className="ri-double-quotes-l text-gray-600 text-sm"></i>
                      </button>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      placeholder="<h3>Nome do Produto</h3><p>Descrição detalhada...</p><ul><li>Característica 1</li><li>Característica 2</li></ul>"
                      rows={8}
                      className="w-full px-4 py-3 border-0 text-sm font-mono focus:outline-none resize-y bg-white"
                    />
                  </div>
                )}

                {descricaoMode !== 'preview' && (
                  <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    {descricaoMode === 'visual'
                      ? 'Edite o texto visualmente. Selecione o texto e use os botões da toolbar.'
                      : 'Edite o HTML diretamente. Use os botões da toolbar para inserir tags.'}
                  </p>
                )}
              </div>

              {/* Submit */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-lg bg-donnos-green hover:bg-donnos-green-dark text-white text-sm font-medium transition-colors"
                >
                  {editingId ? 'Salvar alterações' : 'Cadastrar produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gerenciar Categorias */}
      {showCategoriaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {novaCategoriaNome ? 'Criar Categoria' : 'Gerenciar Categorias'}
              </h3>
              <button
                onClick={() => {
                  setShowCategoriaModal(false);
                  setNovaCategoriaNome('');
                }}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Categoria</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novaCategoriaNome}
                    onChange={(e) => setNovaCategoriaNome(e.target.value)}
                    placeholder="Nome da categoria"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCriarCategoria();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCriarCategoria}
                    disabled={!novaCategoriaNome.trim()}
                    className="px-4 py-2.5 rounded-lg bg-donnos-green hover:bg-donnos-green-dark text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Criar
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categorias existentes ({categorias.length})
                </label>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {categorias.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">Nenhuma categoria cadastrada</p>
                  ) : (
                    categorias.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">{cat.nome}</span>
                        <button
                          type="button"
                          onClick={() => handleDeleteCategoria(cat.id)}
                          className="w-7 h-7 rounded hover:bg-red-50 flex items-center justify-center transition-colors"
                          title="Excluir categoria"
                        >
                          <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowCategoriaModal(false);
                  setNovaCategoriaNome('');
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Configurar WhatsApp */}
      {showWhatsappModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Configurar WhatsApp</h3>
              <button
                onClick={() => setShowWhatsappModal(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número do WhatsApp
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+</span>
                  <input
                    type="text"
                    value={tempWhatsapp}
                    onChange={(e) => setTempWhatsapp(e.target.value.replace(/\D/g, ''))}
                    placeholder="5511999999999"
                    className="w-full pl-7 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-donnos-green focus:ring-1 focus:ring-donnos-green"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Formato: DDI + DDD + número (ex: 5511999999999)
                </p>
              </div>
              <button
                type="button"
                onClick={handleSaveWhatsapp}
                disabled={tempWhatsapp.length < 10}
                className="w-full px-4 py-2.5 rounded-lg bg-donnos-green hover:bg-donnos-green-dark text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-whatsapp-line mr-1.5"></i>
                Salvar número
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
